/**
 * React-binding op `NotificationStore`.
 *
 * Spec: specs/20260831-domotica-meldingen-bewonerstablet §6.0
 * Taak: T-1.21
 *
 * De hook doet twee dingen en verder niets: hij geeft de store een API-laag, en hij leest.
 * Alle logica (poll, verval, bevestigingen, persistentie) staat op moduleniveau in de
 * store, zodat een re-render of een effect-cleanup hem niet kan doden — hetzelfde ontwerp
 * als `useMqttCommands.ts`, en om dezelfde reden.
 */

import { useSyncExternalStore, useEffect, useMemo } from 'react';
import { useAuth } from './useAuth';
import { useTranslation } from './useTranslation';
import * as NotificationStore from '../services/NotificationStore';
import * as VoiceCache from '../services/VoiceCache';
import type { StoredNotification } from '../services/NotificationStore';

/**
 * DE WEG TERUG NAAR DE SERVER — en waarom die NIET aan een pagina mag hangen.
 *
 * ── DE FOUT DIE HIER ZAT ───────────────────────────────────────────────────
 *
 * Dit stond in `useResidentNotifications()`, en die hook werd door precies één component
 * gemount: `NotificationsPage`. Een melding komt echter binnen via MQTT, en die weg loopt
 * langs `useMqttCommands` → `NotificationStore.ingest()` — zonder transport. Op een tablet
 * die op het startscherm stond, verscheen de melding dus volledig correct terwijl de store
 * geen enkele weg terug had.
 *
 * De twee bewakingen die daarop afgaan, staan allebei op `!transport`:
 *
 *   flushAcks()          → keert meteen terug  → de aflevering blijft eeuwig op `pushed`
 *   invokeServerAction() → `code: 'offline'`   → de knop toont "Geen verbinding"
 *
 * Eén oorzaak, twee symptomen die er niets mee te maken lijken te hebben. Wie eerst de
 * meldingenlijst opende en dán belde, zag alles werken — vandaar dat dit zo grillig leek.
 *
 * ── DAAROM HANGT HIJ NU AAN DE APP ─────────────────────────────────────────
 *
 * `App.tsx` mount dit naast `<NotificationFullPage />`. De regel die eronder ligt: een
 * meldingenstore die zonder gemonteerd component moet kunnen werken — dat is de hele reden
 * dat hij op moduleniveau staat — mag zijn transportlaag niet van een ROUTE krijgen.
 *
 * `configure()` is idempotent; een tweede aanroep vervangt alleen de transportlaag en start
 * geen tweede timer. De meldingenpagina mag hem dus gerust ook aanroepen.
 */
/** De API-basis van de ene client (`hooks/useAuth.ts`), voor absolute audio-URL's. */
function apiBaseUrl(): string {
  return (import.meta as any).env?.VITE_API_URL || 'https://api.caro.care';
}

export function useNotificationTransport(): void {
  const { api, isAuthenticated } = useAuth();
  // T-5.02 — `ttsLang: 'auto'` betekent "de taal van de unit/persoon", en dat is precies de
  // taal waarin deze tablet al praat (`person.data.gameLanguage`). De store kent geen auth,
  // dus hij krijgt hem hier aangereikt — net als de API-laag.
  const { lang } = useTranslation();

  useEffect(() => {
    NotificationStore.setTtsLanguage(lang);
  }, [lang]);

  useEffect(() => {
    // Afgemeld (onAuthFailed of een gewone uitlog): de poll moet mee stoppen. Bewust hier
    // en niet als cleanup van dit effect — deze hook hangt op meer dan één plek, en het
    // unmounten van de meldingenpagina mag de poll van de boot-component niet stilleggen.
    if (!isAuthenticated) {
      NotificationStore.stop();
      VoiceCache.configure(null);
      return;
    }
    // De store kent geen auth en geen axios; hij krijgt drie functies. Dat houdt hem
    // testbaar zonder de hele authketen te moeten nabouwen.
    NotificationStore.configure({
      get: (url) => api.get(url),
      post: (url, body) => api.post(url, body),
      // De geluidsroute zit achter `deviceTokenAuth`, dus de bytes moeten langs dezelfde
      // client als de rest. `responseType: 'blob'` gaat door naar axios en levert de bytes.
      getBlob: (url) => api.get<Blob>(url, { responseType: 'blob' }),
    });
    // Spraakmeldingen fase 1 (T091): de spraakclips langs DEZELFDE geauthenticeerde client.
    // `baseUrl` maakt de audio-URL absoluut — nooit een kale relatieve `/api`-fetch naar de
    // app-host (zie SoundCache, 2026-09-24). Het tijdsignaal komt van `timeoutSignal()`.
    VoiceCache.configure({
      baseUrl: apiBaseUrl(),
      getJson: (url) => api.get(url),
      getBlob: (url, init) => api.get<Blob>(url, { responseType: 'blob', signal: init && init.signal }),
    });
  }, [api, isAuthenticated]);

  // Voorladen bij opstart (na aanmelding) en bij elke taalwissel, in de app-taal. Nooit
  // geawait: een clip die er niet is, is gewoon de volgende trede van de ladder.
  useEffect(() => {
    if (!isAuthenticated) return;
    void VoiceCache.preload(lang);
  }, [api, isAuthenticated, lang]);
}

export function useResidentNotifications() {
  useNotificationTransport();

  const items = useSyncExternalStore(
    NotificationStore.subscribe,
    NotificationStore.getSnapshot,
    NotificationStore.getSnapshot,
  );

  // Afgeleid uit dezelfde snapshot, zodat de badge en de lijst nooit uit elkaar kunnen
  // lopen: één bron, één rendermoment.
  const unreadCount = useMemo(
    () => items.reduce((n: number, i: StoredNotification) => (i.read ? n : n + 1), 0),
    [items],
  );

  return {
    notifications: items,
    unreadCount,
    markDisplayed: NotificationStore.markDisplayed,
    markAcknowledged: NotificationStore.markAcknowledged,
    refresh: NotificationStore.refresh,
  };
}

/**
 * Alleen de teller, voor de HomePage-tegel.
 *
 * Apart zodat de HomePage niet op elke lijstwijziging opnieuw rendert wanneer alleen een
 * detail van een melding verandert die hij toch niet toont.
 */
export function useUnreadNotificationCount(): number {
  const items = useSyncExternalStore(
    NotificationStore.subscribe,
    NotificationStore.getSnapshot,
    NotificationStore.getSnapshot,
  );
  return useMemo(
    () => items.reduce((n: number, i: StoredNotification) => (i.read ? n : n + 1), 0),
    [items],
  );
}

/**
 * De melding die op dit moment het scherm mag claimen — of `null`.
 *
 * Spec: FR-16 / plan §6.5. Taak: T-2.05.
 *
 * ⚠ ÉÉN tegelijk, hoogste prioriteit wint, `low` claimt nooit. De rest blijft in de lijst
 * staan en telt mee in de badge. Een wachtrij van dialogen op een wandtablet is een tablet
 * die zijn bewoner gijzelt tot hij alles heeft weggeklikt — en dat is precies het publiek
 * dat daar het slechtst mee omgaat.
 *
 * De selectie zelf staat in de store, niet hier: hij moet ook kloppen wanneer er geen
 * enkel component gemonteerd is (een melding die binnenkomt tijdens een oproep).
 */
export function useClaimedNotification() {
  return useSyncExternalStore(
    NotificationStore.subscribe,
    NotificationStore.getClaimedSnapshot,
    NotificationStore.getClaimedSnapshot,
  );
}
